// /////////////////////////////////////////////////////////////
// [ファイル] ConfirmProvider.tsx
// [概要] 確認ダイアログを命令的に呼び出すためのコンテキスト。
//        useConfirm() が返す confirm() を await すると、ユーザーが
//        「実行」を押したとき true、「キャンセル」/閉じたとき false を返す。
//        window.confirm() の置き換えとして 1 行で利用できる。
//
// 使用例:
//   const confirm = useConfirm();
//   const ok = await confirm({ title: '削除の確認', message: '削除しますか？', destructive: true });
//   if (!ok) return;
// /////////////////////////////////////////////////////////////
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog, { ConfirmOptions } from '../components/ConfirmDialog';

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(async () => false);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ message: '' });
  // 直近の confirm() 呼び出しに対する解決関数を保持する。
  const resolverRef = useRef<(value: boolean) => void>(() => {});

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    resolverRef.current(true);
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
    resolverRef.current(false);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={open}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        {...options}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * 確認ダイアログを命令的に表示するフック。
 * 戻り値の関数を await すると boolean が得られる。
 */
export const useConfirm = () => useContext(ConfirmContext);
